/**
 * useStaff — staff + group data hook backed by @v0y4ge/api-client.
 * No seed data in the UI. All data comes from the contract layer.
 *
 * Restored 2026-07-03 (Claude Code push-back, drop blocker 1a): the previous
 * revision narrowed this hook to list-only local types and dropped the
 * canonical contract imports + mutation calls. The create/update/delete/
 * duplicate operations below all exist in @v0y4ge/api-contracts + the real
 * backend today — this hook now calls them for real, with an optimistic
 * local fallback ONLY for the preview harness (whose fixture stub doesn't
 * implement staff mutations yet — see index.html STUBS).
 */
import { useState, useEffect } from "react";
import { T } from "@v0y4ge/jrni-ui";
import type { CanonicalStaffMember, CanonicalStaffGroup, StaffLocationAssignment } from "@v0y4ge/api-contracts";
import { logUpsert, logDelete } from "../../lib/audit";
import {
  listStaff, listStaffGroups,
  createStaff, updateStaff, deleteStaff, duplicateStaff, setStaffLocationRoles,
  createStaffGroup, updateStaffGroup, deleteStaffGroup, duplicateStaffGroup,
} from "@v0y4ge/api-client";
import { can, onTenantChange, onTenantSwitching } from "../../shell/access";

// UI-facing shapes are the canonical contract types (byte-for-byte — no local
// re-declaration to drift out of sync). `updated`/`services` below are the
// adapter's derived convenience fields the screens already read.
export type StaffMember = CanonicalStaffMember & { services?: number; resourceIds?: string[] };
export type StaffGroup = CanonicalStaffGroup;

function toMember(m: Record<string, any>): StaffMember {
  const first = m.firstName ?? m.first_name ?? "";
  const last = m.lastName ?? m.last_name ?? "";
  const photoUrl = m.photoUrl ?? m.photo_url ?? m.imageUrl ?? m.image_url ?? m.photo ?? undefined;
  return {
    id: m.id, staffId: m.staffId ?? m.staff_id ?? m.id,
    firstName: first, lastName: last,
    name: m.name ?? `${first} ${last}`.trim(),
    email: m.email ?? "", phone: m.phone ?? undefined, title: m.title ?? m.role ?? "",
    role: m.role ?? m.title ?? null,
    bio: m.bio ?? "",
    photo: m.photo ?? photoUrl ?? undefined,
    photoUrl: photoUrl ?? null,
    status: m.status ?? "active", signsIn: m.signsIn ?? m.signs_in ?? true,
    channels: m.channels ?? [], groups: m.groups ?? [],
    locations: m.locations ?? (m.location ?? m.locationName ? [m.location ?? m.locationName] : undefined),
    serviceGrants: m.serviceGrants ?? m.service_grants ?? [],
    serviceExclusions: m.serviceExclusions ?? m.service_exclusions ?? [],
    resourceIds: m.resourceIds ?? m.resource_ids ?? [],
    skills: m.skills ?? [], languages: m.languages ?? [],
    department: m.department ?? null, category: m.category ?? null,
    tags: m.tags ?? [],
    concurrency: m.concurrency ?? null, dailyCap: m.dailyCap ?? m.daily_cap ?? null,
    timeOff: m.timeOff ?? m.time_off ?? [],
    /* Default display zone (2026-08-07, Pat) — IANA id or null; null means
       "fall back to the workspace display zone", never "browser zone". */
    timezone: m.timezone ?? m.time_zone ?? null,
    slug: m.slug ?? undefined, linkEnabled: m.linkEnabled ?? m.link_enabled ?? false,
    color: m.color ?? undefined,
    createdAt: m.createdAt ?? m.created_at ?? undefined,
    updatedAt: m.updatedAt ?? m.updated_at ?? undefined,
    services: m.services ?? undefined,
  } satisfies StaffMember;
}
function toGroup(g: Record<string, any>): StaffGroup {
  return {
    id: g.id, name: g.name ?? "", description: g.description ?? "", hue: g.hue ?? g.color ?? T.p1,
    status: g.status ?? "published", services: g.services ?? [], memberCount: g.memberCount ?? 0,
    updatedAt: g.updatedAt ?? g.updated_at ?? undefined,
  } as StaffGroup;
}

/**
 * Shared cache — hydrated from the typed client; mutations call the real
 * create/update/delete/duplicate operations and reconcile the response into
 * the cache (so the list, groups, and editor screens stay consistent within
 * a session; full server-side persistence is what these calls now provide).
 */
interface StaffState { members: StaffMember[]; groups: StaffGroup[]; }
let _cache: StaffState | null = null;
let _loading = false;
/** Set only when a list read FAILED — never for an empty roster (2026-07-31 rule;
 *  screens pass this to <LoadState> so an outage cannot look like "no staff"). */
let _error: string | null = null;
const _listeners = new Set<() => void>();
const _notify = () => _listeners.forEach((fn) => fn());
// Team roster leak fix (docket 2026-07-26): purge + refetch on tenant switch.
// Cross-tenant leak fix (docket 2026-07-27, item 1): purge SYNCHRONOUSLY the
// instant a switch starts (`_loading = true` also BLOCKS an incidental
// hydrate() call during the pending window); (re)hydrate only once the
// tenant is CONFIRMED — a fetch fired any earlier would still run under the
// outgoing tenant's session against a real backend.
onTenantSwitching(() => { _cache = null; _loading = true; _notify(); });
onTenantChange(() => { _loading = false; if (can("team.read")) hydrate(); }); // admin/staff — gated (docket 2026-09-17); Staff route is team.read
function _set(patch: Partial<StaffState>) {
  _cache = { members: _cache?.members ?? [], groups: _cache?.groups ?? [], ...patch };
  _notify();
}
function hydrate(done?: () => void) {
  if (_loading) { done?.(); return; }
  _loading = true;
  /* A failed list read must be distinguishable from an empty roster (2026-07-31
     rule — see shell/composites LoadState). Recorded per call so a partial
     failure says which half is missing rather than blanking the screen. */
  _error = null;
  Promise.all([
    (typeof listStaff === "function" ? listStaff({ limit: String(200) }) : Promise.resolve({ data: [], staff: [], members: [] })).catch((e) => { console.warn("listStaff failed:", e); _error = "Couldn't load staff."; return { data: [], staff: [], members: [] }; }),
    (typeof listStaffGroups === "function" ? listStaffGroups() : Promise.resolve({ data: [], groups: [] })).catch((e) => { console.warn("listStaffGroups failed:", e); _error = _error ? "Couldn't load staff or groups." : "Couldn't load staff groups."; return { data: [], groups: [] }; }),
  ]).then(([mr, gr]) => {
    const ms = ((mr as any).data ?? (mr as any).staff ?? (mr as any).members ?? []) as any[];
    const gs = ((gr as any).data ?? (gr as any).groups ?? []) as any[];
    _cache = { members: ms.map(toMember), groups: gs.map(toGroup) };
    _loading = false;
    _notify();
    done?.();
  }).catch((e) => { console.warn("staff hydrate failed:", e); _error = "Couldn't load staff."; _cache = { members: [], groups: [] }; _loading = false; _notify(); done?.(); });
}

export function useStaff() {
  const [, force] = useState(0);
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _listeners.add(fn);
    if (_cache === null && !_loading) hydrate(() => setLoading(false));
    else setLoading(false);
    return () => { _listeners.delete(fn); };
  }, []);

  const state = _cache ?? { members: [], groups: [] };
  return {
    members: state.members,
    groups: state.groups,
    loading,
    /** Failed read — pass to <LoadState>; never render an empty roster over this. */
    error: _error,
    /** Create (no id yet) or update (existing id) a staff member via the real API. */
    upsertMember: async (m: StaffMember) => {
      const members = _cache?.members ?? [];
      const isNew = !members.some((x) => x.id === m.id);
      /* `role` + `locations` were MISSING here (fixed 2026-08-06): the editor let
         you pick a Studio role and build a per-location schedule, the payload
         dropped both, and the response echoed back without them — so the screen
         silently reverted. Both are declared on CanonicalStaffMember and on the
         create/update payloads (api-contracts/src/staff.ts), so this is a
         send-what-the-contract-accepts fix, not a new field. */
      const input = { firstName: m.firstName, lastName: m.lastName, email: m.email, phone: m.phone, title: m.title, role: m.role ?? null, status: m.status, signsIn: m.signsIn, channels: m.channels, groups: m.groups, locations: m.locations, concurrency: m.concurrency, dailyCap: m.dailyCap, photo: m.photo, photoUrl: m.photoUrl ?? (m.photo || null), color: (m as any).color, serviceGrants: m.serviceGrants, resourceIds: m.resourceIds };
      /* `timezone` is deliberately NOT in this payload (re-applied 2026-09-08).
         No staff write schema accepts it, so sending it either 400s the write or
         is dropped silently — and the second is worse: the toast reports success
         over a field that reverts on the next read. It is read on the way IN
         (`toMember` maps timezone/time_zone) because the server does serve it;
         a write needs a contract field to land in, and there is not one yet. */
      let saved: StaffMember = m;
      // Addendum item 7: await-before-commit — a rejected write must NOT be
      // "applied optimistically" behind a success toast (UIR-21 class).
      if (isNew && typeof createStaff === "function") saved = toMember(await createStaff(input as any));
      else if (!isNew && typeof updateStaff === "function") saved = toMember(await updateStaff(m.id, input as any));
      logUpsert("staff", isNew ? null : (members.find((x) => x.id === m.id) as unknown as Record<string, unknown>), saved as unknown as Record<string, unknown>, { name: [saved.firstName, saved.lastName].filter(Boolean).join(" ") || saved.email });
      const next = isNew ? [saved, ...members] : members.map((x) => (x.id === m.id ? saved : x));
      _set({ members: next });
    },
    removeMember: async (id: string) => {
      if (typeof deleteStaff === "function") await deleteStaff(id);
      const prev = (_cache?.members ?? []).find((x) => x.id === id);
      if (prev) logDelete("staff", prev as unknown as Record<string, unknown>, { name: [prev.firstName, prev.lastName].filter(Boolean).join(" ") || prev.email });
      _set({ members: (_cache?.members ?? []).filter((x) => x.id !== id) });
    },
    duplicateMember: async (id: string) => {
      try {
        if (typeof duplicateStaff === "function") {
          const copy = toMember(await duplicateStaff(id));
          _set({ members: [copy, ...(_cache?.members ?? [])] });
          return copy;
        }
      } catch (e) { console.warn("duplicateStaff failed:", e); }
      return null;
    },
    /**
     * Replace where a staff member works and the role they hold at each
     * (2026-08-08, Staff & Roles — `setStaffLocationRoles`). The whole set goes
     * every time, so dropping a location is an ordinary edit. Awaited before
     * the cache moves: a rejected write must never leave the screen showing a
     * saved state (no-fake-interactivity).
     */
    setLocationRoles: async (staffId: string, assignments: StaffLocationAssignment[]) => {
      if (typeof setStaffLocationRoles !== "function") throw new Error("setStaffLocationRoles is not available in this build.");
      const res = await setStaffLocationRoles(staffId, assignments) as unknown as Record<string, any>;
      const saved = toMember(res?.data ?? res?.staff ?? res);
      const prev = (_cache?.members ?? []).find((x) => x.id === staffId) ?? null;
      logUpsert("staff", prev as unknown as Record<string, unknown> | null, saved as unknown as Record<string, unknown>, { name: saved.name || saved.email });
      _set({ members: (_cache?.members ?? []).map((x) => (x.id === staffId ? saved : x)) });
      return saved;
    },
    /**
     * The person's DEFAULT role — what every location inherits unless it sets
     * its own (operator, 2026-08-08). Stored on the roster record's `role`,
     * which is where a person with no workspace membership can hold one too.
     * Awaited before the cache moves.
     */
    setDefaultRole: async (staffId: string, role: string | null) => {
      if (typeof updateStaff !== "function") throw new Error("updateStaff is not available in this build.");
      const saved = toMember(await updateStaff(staffId, { role } as never) as unknown as Record<string, any>);
      _set({ members: (_cache?.members ?? []).map((x) => (x.id === staffId ? saved : x)) });
      return saved;
    },
    upsertGroup: async (g: StaffGroup) => {
      const groups = _cache?.groups ?? [];
      const isNew = !groups.some((x) => x.id === g.id);
      const input = { name: g.name, description: g.description, hue: g.hue, status: g.status, services: g.services };
      let saved: StaffGroup = g;
      if (isNew && typeof createStaffGroup === "function") saved = toGroup(await createStaffGroup(input as any));
      else if (!isNew && typeof updateStaffGroup === "function") saved = toGroup(await updateStaffGroup(g.id, input as any));
      logUpsert("staffGroup", isNew ? null : (groups.find((x) => x.id === g.id) as unknown as Record<string, unknown>), saved as unknown as Record<string, unknown>, { name: saved.name });
      const next = isNew ? [saved, ...groups] : groups.map((x) => (x.id === g.id ? saved : x));
      _set({ groups: next });
    },
    removeGroup: async (id: string) => {
      if (typeof deleteStaffGroup === "function") await deleteStaffGroup(id);
      const prev = (_cache?.groups ?? []).find((x) => x.id === id);
      if (prev) logDelete("staffGroup", prev as unknown as Record<string, unknown>, { name: prev.name });
      _set({ groups: (_cache?.groups ?? []).filter((x) => x.id !== id) });
    },
    duplicateGroup: async (id: string) => {
      try {
        if (typeof duplicateStaffGroup === "function") {
          const copy = toGroup(await duplicateStaffGroup(id));
          _set({ groups: [copy, ...(_cache?.groups ?? [])] });
          return copy;
        }
      } catch (e) { console.warn("duplicateStaffGroup failed:", e); }
      return null;
    },
    reload: () => { _cache = null; hydrate(); },
  };
}
